import { useEffect } from 'react';
import Raven from 'raven-js';
import { ACTION_CONTEXTS, FintechOutageBannerWithFetch
// @ts-expect-error ts-migrate(7016)
} from 'account-and-billing-fintech-outages';
import { Flex } from 'foundations-components/components/Flex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ActiveQuoteBanner from 'pricing-pages-ui-components/banners/ActiveQuoteBanner';
import { CommerceHubEarlyAdopterBanner } from 'pricing-pages-ui-components/banners/CommerceHubEarlyAdopterBanner';
import PartnerProgramBanner from 'pricing-pages-ui-components/banners/PartnerProgramBanner';
import PromotionBanner from 'pricing-pages-ui-components/banners/PromotionBanner';
import RussianBlockedBanner from 'pricing-pages-ui-components/banners/RussianBlockedBanner';
import { StarterPromotion2025Banner } from 'pricing-pages-ui-components/banners/StarterPromotion2025Banner';
import { getPartnerProgramUrl } from 'pricing-pages-ui-components/utils/partnerProgramUtils';
import { useActiveQuoteBanner } from '../hooks/banners/useActiveQuoteBanner';
import { useCommerceHubEarlyAdopterBanner } from '../hooks/banners/useCommerceHubEarlyAdopterBanner';
import { usePartnerProgramBanner } from '../hooks/banners/usePartnerProgramBanner';
import { useStarterPromo2025Banner } from '../hooks/banners/useStarterPromo2025Banner';
// TODO: Replace this trackBannerInteraction once we have v2 tracking
// @temporary https://git.hubteam.com/HubSpot/pricing-pages-unified-ui/issues/14182
// @expiration 2025-11-01
// @deprecated
import { trackBannerInteraction, trackClickedLink } from '../../v2/utils/tracking/trackingEventUtils';
import { usePromotionBanners } from '../hooks/banners/usePromotionBanners';
import { useRussianBlockedBanner } from '../hooks/banners/useRussianBlockedBanner';
import { getActiveQuoteUrl, handleRedirect } from '../utils/urls/urls';
import { useIsInAppAuth } from '../hooks/user/useUserInfo';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const BannerSection = ({
  screen,
  tab,
  apiNames
}) => {
  const isInAppAuth = useIsInAppAuth();
  const shouldShowRussianBlockedBanner = useRussianBlockedBanner();
  const {
    shouldShowBanner: shouldShowStarterPromoBanner,
    error: starterPromoError,
    annualPrice: starterAnnualPrice,
    monthlyPrice: starterMonthlyPrice,
    currencyCode
  } = useStarterPromo2025Banner({
    screen,
    tab
  });
  const {
    shouldShowBanner: shouldShowCommerceBanner,
    professionalPrice,
    enterprisePrice,
    professionalNonDiscountedPrice,
    enterpriseNonDiscountedPrice,
    currencyCode: commerceCurrencyCode,
    error: commerceError
  } = useCommerceHubEarlyAdopterBanner();
  const {
    shouldShowPartnerProgramBanner,
    dismissBanner
  } = usePartnerProgramBanner();
  const {
    bannerConfigs,
    error: promotionError
  } = usePromotionBanners();
  const {
    shouldShowActiveQuoteBanner,
    activeQuotePublicId,
    handleClickClose
  } = useActiveQuoteBanner({
    apiNames
  });
  useEffect(() => {
    if (starterPromoError) {
      Raven.captureException(starterPromoError);
    }
    if (promotionError) {
      Raven.captureException(promotionError);
    }
    if (commerceError) {
      Raven.captureException(commerceError);
    }
  }, [starterPromoError, promotionError, commerceError]);

  // Skip rendering banners entirely for public pages
  // Most banner hooks rely on authenticated endpoints or in-app user data
  // TODO: Re-enable specific banners for public when endpoints support it
  if (!isInAppAuth) {
    return null;
  }
  const PromotionBannerSection = () => {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: bannerConfigs.map(bannerConfig => {
        if (bannerConfig.shouldShow) {
          return /*#__PURE__*/_jsx(PromotionBanner, {
            promotion: bannerConfig.promotion,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: bannerConfig.titleMessage
            }),
            description: /*#__PURE__*/_jsx(FormattedMessage, {
              message: bannerConfig.descriptionMessage
            })
          }, bannerConfig.promotion);
        }
        return null;
      })
    });
  };
  const BannerHierarchySection = () => {
    if (shouldShowRussianBlockedBanner) {
      return /*#__PURE__*/_jsx(RussianBlockedBanner, {});
    }
    if (shouldShowCommerceBanner && commerceCurrencyCode) {
      return /*#__PURE__*/_jsx(CommerceHubEarlyAdopterBanner, {
        professionalPrice: professionalPrice,
        enterprisePrice: enterprisePrice,
        professionalNonDiscountedPrice: professionalNonDiscountedPrice,
        enterpriseNonDiscountedPrice: enterpriseNonDiscountedPrice,
        currencyCode: commerceCurrencyCode,
        trackBannerInteraction: trackBannerInteraction
      });
    }
    if (shouldShowStarterPromoBanner) {
      return /*#__PURE__*/_jsx(StarterPromotion2025Banner, {
        currencyCode: currencyCode,
        annualPrice: starterAnnualPrice,
        monthlyPrice: starterMonthlyPrice,
        trackBannerInteraction: trackBannerInteraction
      });
    }
    return null;
  };
  const InAppBannerSection = () => {
    if (shouldShowPartnerProgramBanner) {
      return /*#__PURE__*/_jsx(PartnerProgramBanner, {
        onLinkClick: () => trackClickedLink({
          name: 'partner-banner-more-info',
          value: 'Click here',
          type: 'external',
          url: getPartnerProgramUrl()
        }),
        onClose: dismissBanner
      });
    }
    if (shouldShowActiveQuoteBanner) {
      return /*#__PURE__*/_jsx(ActiveQuoteBanner, {
        handleClickQuote: () => {
          if (activeQuotePublicId) {
            handleRedirect(getActiveQuoteUrl(activeQuotePublicId));
          }
        },
        handleClickClose: handleClickClose,
        trackBannerInteraction: trackBannerInteraction
      });
    }
    return null;
  };
  return /*#__PURE__*/_jsxs(Flex, {
    display: "flex",
    flexDirection: "column",
    inlineSize: "100%",
    rowGap: "400",
    marginBlockEnd: "400",
    "data-test-id": "banner-section-container",
    children: [/*#__PURE__*/_jsx(FintechOutageBannerWithFetch, {
      actionContext: ACTION_CONTEXTS.PURCHASE
    }), /*#__PURE__*/_jsx(PromotionBannerSection, {}), /*#__PURE__*/_jsx(BannerHierarchySection, {}), /*#__PURE__*/_jsx(InAppBannerSection, {})]
  });
};